import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { FilterChips } from '~/components/Shared/Filters/FilterChips/FilterChips';
import {
  addFilters,
  clearFilter,
  removeFilter,
  updateFilter,
} from '~/components/Shared/Filters/Filters.utils';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { LoadingShimmer } from '~/eds/src/components/LoadingShimmer/LoadingShimmer';
import { Filter } from '~/evifields/src/types/Filter';
import { FiltersCount, SearchFilter } from '~/types/DashboardV2';
import { PilotId } from '~/types/PilotId';

import { CrossFilters } from '../cross-filters/CrossFilters';
import { ManageFilters } from '../manage-filters/ManageFilters';
import { sortFilters } from './Filters.utils';

type Props = {
  crossFilters?: Filter[];
  isLoading?: boolean;
  mode?: 'preview';
  selectedFilters: Filter[];
  searchFilters: SearchFilter[];
  pinnedFiltersIds?: Array<PilotId | string>;
  placeholder?: string;
  readOnly?: boolean;
  onChange: (filters: Filter[]) => void;
  onChangeCrossFilters?: (crossFilters: Filter[]) => void;
  onFiltersReset?: () => void;
  onQueryButtonClick?: () => void;
  onUpdatePinnedFilters?: (
    updatedPinnedFilters: Array<PilotId | string>,
  ) => void;
};
export const Filters = ({
  crossFilters = [],
  isLoading = false,
  mode,
  pinnedFiltersIds = [],
  placeholder,
  readOnly = false,
  selectedFilters,
  searchFilters,
  onUpdatePinnedFilters,
  onQueryButtonClick,
  onFiltersReset,
  onChange,
  onChangeCrossFilters,
}: Props) => {
  const sortedFilters = useMemo(
    () => sortFilters(selectedFilters, pinnedFiltersIds),
    [selectedFilters, pinnedFiltersIds],
  );
  const intl = useIntl();
  const getActionButtonTooltip = ({ selectedFiltersCount }: FiltersCount) => {
    if (selectedFiltersCount === 0) {
      return intl.formatMessage({
        id: 'Filters.selectAtLeastOneFilter',
        defaultMessage: 'Please select at least one filter.',
        description: 'Tooltip when no filters are selected',
      });
    }
    return '';
  };

  const isPreviewMode = mode === 'preview';
  const previewStyle = isPreviewMode
    ? {
        wrap: true,
        wrapSpacing: '2',
      }
    : { justify: 'space-between' };

  return (
    <Layout spacing="1" wrap minW={0} {...previewStyle}>
      <Layout
        spacing="1"
        minW={0}
        wrap
        role="list"
        wrapSpacing={1}
        aria-label={intl.formatMessage({
          id: 'Filters.pinnedFiltersLabel',
          defaultMessage: 'pinned',
          description: 'Aria label for pinned filters',
        })}
        align="center"
      >
        {!readOnly && (
          <Box>
            <ManageFilters
              preset="add-filters-button"
              getActionButtonTooltip={getActionButtonTooltip}
              groupsOrder={[]}
              collapsible={true}
              onApply={(selectedFilterIds) =>
                onChange(addFilters(selectedFilters, selectedFilterIds))
              }
              onOpenQueryBuilder={onQueryButtonClick}
              pinnedFiltersIds={pinnedFiltersIds}
              searchFilters={searchFilters}
              onUpdatePinnedFilters={onUpdatePinnedFilters}
            />
          </Box>
        )}
        {isLoading ? (
          <>
            {Array.from({ length: 6 }).map((_, index) => (
              <LoadingShimmer
                key={`filter_loading_${index}`}
                height="26px"
                width="200px"
              />
            ))}
          </>
        ) : (
          <FilterChips
            pinnedFiltersIds={pinnedFiltersIds}
            enableFilterViews
            disableRemove={false}
            placeholder={placeholder}
            searchFilters={searchFilters}
            readOnly={readOnly}
            filters={sortedFilters}
            onRemoveFilter={(filterToRemove) =>
              onChange(removeFilter(selectedFilters, filterToRemove))
            }
            onUpdateFilter={(updatedFilter) =>
              onChange(updateFilter(selectedFilters, updatedFilter))
            }
          />
        )}
        {onChangeCrossFilters && (
          <CrossFilters
            searchFilters={searchFilters}
            filters={crossFilters}
            onChange={onChangeCrossFilters}
          />
        )}
      </Layout>
      {!readOnly && (
        <Layout alignSelf="flex-start" ml={isPreviewMode ? '-13px' : undefined}>
          <Button
            iconPosition="left"
            icon="reload"
            variant="action"
            text={intl.formatMessage({
              id: 'Filters.resetFiltersButton',
              defaultMessage: 'Reset Filters',
              description: 'Button text to reset all filters',
            })}
            onClick={() => {
              if (onFiltersReset) {
                onFiltersReset();
              } else {
                onChange(
                  selectedFilters
                    .map(clearFilter)
                    .filter((filter) =>
                      pinnedFiltersIds.includes(filter.fieldId),
                    ),
                );
              }
            }}
          />
        </Layout>
      )}
    </Layout>
  );
};
